import { TranslateService } from "@ngx-translate/core";

/**
 * QSL-11284 — surface the server's per-field validation errors instead of one
 * opaque, untranslated banner.
 *
 * The NestJS `CustomValidationPipe` returns
 *   `{ message: 'Input data validation failed', errors: { field: 'reason', ... } }`
 * on a 400. The teacher client used to show only `message` (a hardcoded English
 * string) and discard `errors` — so a validation failure named no field. A real
 * teacher was blocked in onboarding for four days because support couldn't tell
 * which field the server rejected.
 *
 * These helpers turn the `errors` map into a self-diagnosing, translated
 * message. Field keys may be dotted (the pipe walks nested DTO children and
 * joins with '.'), so `humanizeFieldKey` handles dotted camelCase. The
 * per-field reasons themselves are class-validator's English constraint
 * strings; we name the field (the piece support actually needed) and show the
 * reason alongside it.
 */

export interface IServerFieldError {
  /** Raw field key from the server, e.g. "whatsappNumber" or "additionalInfo.phone". */
  field: string;
  /** Humanized label, e.g. "Whatsapp Number" or "Additional Info › Phone". */
  label: string;
  /** The server's constraint message (English, from class-validator). */
  message: string;
}

/** "Input data validation failed" translation key (teacher scope, ui_translation). */
export const VALIDATION_FAILED_KEY = "teacher_validation_failed";

/** Turn a (possibly dotted) camelCase/snake field key into a readable label. */
export function humanizeFieldKey(key: string): string {
  return key
    .split(".")
    .map((segment) =>
      segment
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    )
    .filter((segment) => segment.length > 0)
    .join(" › ");
}

/** Extract the per-field errors from an IError-shaped payload (safe on nulls). */
export function extractServerFieldErrors(payload: IError): IServerFieldError[] {
  const errors = payload?.errors;
  if (!errors || typeof errors !== "object") {
    return [];
  }
  return Object.keys(errors).map((field) => ({
    field,
    label: humanizeFieldKey(field),
    message: String(errors[field]),
  }));
}

/**
 * Build the user-facing validation banner: a translated header followed by the
 * names of the offending fields, so the teacher (and support) can see exactly
 * what to fix. Degrades safely — if the key is not yet seeded, ngx-translate
 * returns the key verbatim, so we fall back to the server's own `message`
 * rather than render a raw `teacher_validation_failed` on screen.
 */
export function buildValidationErrorMessage(
  payload: IError,
  translate: TranslateService
): string {
  const fields = extractServerFieldErrors(payload);

  // Not a per-field validation error (e.g. a 500 or a network failure carries
  // no `errors` map): show the server's own `message` unchanged. Using the
  // validation header here would wrongly tell the teacher to review fields that
  // aren't listed. This preserves today's behaviour for non-validation errors.
  if (!fields.length) {
    return payload?.message || "";
  }

  // ngx-translate returns the key verbatim when it isn't loaded/seeded yet, so
  // fall back to the server message rather than render a raw key on screen.
  const translated = translate.instant(VALIDATION_FAILED_KEY);
  const header =
    translated && translated !== VALIDATION_FAILED_KEY
      ? translated
      : payload?.message || "Please review the highlighted fields.";

  return `${header}: ${fields.map((f) => f.label).join(", ")}`;
}
